import React, { useMemo, useState, useEffect } from 'react';
import { Dropdown, Segmented, Tooltip, Empty, Typography, Divider } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { DateRange } from '../../services/DashboardService';

const { Text } = Typography;

interface WeekSelectorModalProps {
  open: boolean;
  dateRanges: DateRange[];
  selectedRange: DateRange | null;
  onSelect: (range: DateRange) => void;
  onClose: () => void;
  children?: React.ReactNode;
  getBaselinePeriod?: (startDate: string, endDate: string) => { baselineStart: string; baselineEnd: string; validStart: string; validEnd: string } | null;
  formatDisplayDate?: (value?: string) => string;
}

interface WeekTile {
  id: string;
  label: string;
  dateRange: DateRange;
  isoWeek: number | null;
  tooltip: string;
  monthLabel: string;
}

const WeekSelectorModal: React.FC<WeekSelectorModalProps> = ({
  open,
  dateRanges,
  selectedRange,
  onSelect,
  onClose,
  children,
  getBaselinePeriod,
  formatDisplayDate,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { years, weeksByYear, initialYear } = useMemo(() => {
    const grouped = new Map<number, WeekTile[]>();
    let defaultYear: number | null = null;

    dateRanges.forEach((range) => {
      const start = parseDate(range.startDate);
      const end = parseDate(range.endDate);
      if (!start || !end) {
        return;
      }

      const year = start.getFullYear();
      // Set default year based on selected range
      if (!defaultYear && selectedRange && range.startDate === selectedRange.startDate) {
        defaultYear = year;
      }

      // Use the actual date range from backend (already Monday-Sunday)
      const isoWeek = getIsoWeek(start);
      const dateRangeLabel = formatDisplayDate ? `${formatDisplayDate(range.startDate)} → ${formatDisplayDate(range.endDate)}` : `${formatDate(start)} → ${formatDate(end)}`;
      const monthLabel = start.toLocaleDateString(undefined, { month: 'long' });
      
      // Calculate baseline for tooltip
      let tooltipText = dateRangeLabel;
      if (getBaselinePeriod && formatDisplayDate) {
        const baselineInfo = getBaselinePeriod(range.startDate, range.endDate);
        if (baselineInfo) {
          tooltipText = `Baseline: ${formatDisplayDate(baselineInfo.baselineStart)} → ${formatDisplayDate(baselineInfo.baselineEnd)}`;
        }
      }
      
      const tile: WeekTile = {
        id: `${range.startDate}-${range.endDate}`,
        label: dateRangeLabel, // Show date range instead of "Week XX"
        dateRange: range,
        isoWeek,
        tooltip: tooltipText,
        monthLabel,
      };

      if (!grouped.has(year)) {
        grouped.set(year, []);
      }
      grouped.get(year)!.push(tile);
    });

    const sortedYears = Array.from(grouped.keys()).sort((a, b) => b - a);

    sortedYears.forEach((year) => {
      const weeks = grouped.get(year)!;
      weeks.sort((a, b) => {
        const aDate = parseDate(a.dateRange.startDate);
        const bDate = parseDate(b.dateRange.startDate);
        if (!aDate || !bDate) return 0;
        return aDate.getTime() - bDate.getTime();
      });
    });

    // Determine the initial year based on selected range or latest/newest selection
    // Priority: selectedRange year > latest dateRange year > current year
    let fallbackYear: number | null = null;
    
    if (selectedRange) {
      const selectedYear = parseDate(selectedRange.startDate)?.getFullYear();
      if (selectedYear) {
        fallbackYear = selectedYear;
      }
    }
    
    // If no selected range, use the latest/newest date range (first in sorted array)
    if (!fallbackYear && sortedYears.length > 0) {
      fallbackYear = sortedYears[0];
    }
    
    // Final fallback to current year
    if (!fallbackYear) {
      fallbackYear = new Date().getFullYear();
    }

    return {
      years: sortedYears,
      weeksByYear: grouped,
      initialYear: fallbackYear,
    };
  }, [dateRanges, selectedRange, getBaselinePeriod, formatDisplayDate]);

  const [activeYear, setActiveYear] = useState<number>(initialYear);

  // Update activeYear when initialYear changes (e.g., when selectedRange changes)
  // Also update when modal opens to ensure correct year is shown
  useEffect(() => {
    if (initialYear && weeksByYear.has(initialYear)) {
      setActiveYear(initialYear);
    } else if (initialYear && years.length > 0) {
      // If initialYear doesn't have weeks, use the closest available year
      const closestYear = years.find(y => y <= initialYear) || years[0];
      setActiveYear(closestYear);
    }
  }, [initialYear, weeksByYear, years, open]);

  const weeksForActiveYear = useMemo(() => {
    if (!weeksByYear.has(activeYear)) {
      return [];
    }
    return weeksByYear.get(activeYear)!;
  }, [activeYear, weeksByYear]);

  const handleYearChange = (value: string | number) => {
    const year = Number(value);
    if (!Number.isNaN(year)) {
      setActiveYear(year);
    }
  };

  const handleSelect = (week: WeekTile) => {
    // Create a new DateRange with the date range label
    const updatedRange: DateRange = {
      ...week.dateRange,
      label: week.label, // Use the date range label (e.g., "Apr 7 → June 1")
    };
    onSelect(updatedRange);
    // Don't close automatically - dropdown will handle that
  };

  const renderWeekGrid = () => {
    if (weeksForActiveYear.length === 0) {
      return (
        <div style={{ padding: '24px 0' }}>
          <Empty description="No weeks available for this year" />
        </div>
      );
    }

    const months = groupByMonth(weeksForActiveYear);

    return (
      <div style={{ maxHeight: 320, overflowY: 'auto', paddingRight: 8 }}>
        {months.map(({ month, weeks }) => (
          <div key={month} style={{ marginBottom: 12 }}>
            <Text strong style={{ marginBottom: 6, display: 'inline-block', fontSize: 13 }}>
              {month}
            </Text>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                  ? 'repeat(auto-fill, minmax(100px, 1fr))'
                  : 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: isMobile ? 4 : 6,
              }}
            >
              {weeks.map((week) => {
                const isSelected =
                  selectedRange?.startDate === week.dateRange.startDate &&
                  selectedRange?.endDate === week.dateRange.endDate;
                return (
                  <Tooltip key={week.id} title={week.tooltip}>
                    <button
                      type="button"
                      onClick={() => handleSelect(week)}
                      className="week-selector-tile"
                      style={{
                        width: '100%',
                        padding: isMobile ? '6px 8px' : '4px 6px',
                        borderRadius: 4,
                        border: isSelected ? '2px solid #1677ff' : '1px solid #d9d9d9',
                        backgroundColor: isSelected ? 'rgba(22, 119, 255, 0.12)' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Text strong style={{ fontSize: isMobile ? 11 : 12, whiteSpace: 'nowrap' }}>
                        {week.label}
                      </Text>
                      <InfoCircleOutlined
                        style={{
                          fontSize: isMobile ? 11 : 12,
                          color: '#8c8c8c',
                          flexShrink: 0,
                          marginLeft: 4,
                        }}
                      />
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const dropdownContent = (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: isMobile ? 12 : 16,
        maxWidth: isMobile ? '90vw' : '600px',
        width: isMobile ? '90vw' : '600px',
        maxHeight: isMobile ? '80vh' : '500px',
        overflowY: 'auto',
      }}
      onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing when clicking inside
    >
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>Select Reporting Week</Text>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>Year:</Text>
        {years.length > 0 ? (
          <Segmented
            size="small"
            options={years.map((year) => ({ label: String(year), value: year }))}
            value={activeYear}
            onChange={handleYearChange}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>No data available</Text>
        )}
      </div>

      <Divider style={{ margin: '8px 0 12px' }} />

      {renderWeekGrid()}
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={(visible) => !visible && onClose()}
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
    >
      {children || <div />}
    </Dropdown>
  );
};

const parseDate = (value: string | undefined): Date | null => {
  if (!value) return null;
  // Parse YYYY-MM-DD format without timezone conversion
  const parts = value.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);
    const date = new Date(year, month, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  // Fallback to standard parsing
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const getIsoWeek = (date: Date): number | null => {
  if (Number.isNaN(date.getTime())) return null;
  const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = tempDate.getUTCDay() || 7;
  tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
  return Math.ceil(((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};


const groupByMonth = (weeks: WeekTile[]): { month: string; weeks: WeekTile[] }[] => {
  const groups = new Map<string, WeekTile[]>();
  weeks.forEach((week) => {
    const month = week.monthLabel;
    if (!groups.has(month)) {
      groups.set(month, []);
    }
    groups.get(month)!.push(week);
  });

  return Array.from(groups.entries()).map(([month, monthWeeks]) => ({
    month,
    weeks: monthWeeks,
  }));
};

export default WeekSelectorModal;


